import { Box, ChevronDown, Key, Loader2, PackageSearch, PlugZap, X } from "lucide-react";
import { Language, dictionary } from "../../../utils/dictionary";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";

const MASTERSHOP_LOGO = INTEGRATIONS_UI_CONFIG.mastershop.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

interface ModalMastershopIntegrationProps {
  setModalOpenMastershop: (open: boolean) => void;
  handlePostMastershopIntegration: (apiKey: string) => void;
  apiKey: string;
  setApiKey: (apiKey: string) => void;
  loading: boolean;
  language: Language;
}

const ModalMastershopIntegration = ({
  setModalOpenMastershop,
  handlePostMastershopIntegration,
  apiKey,
  setApiKey,
  loading,
  language,
}: ModalMastershopIntegrationProps) => {
  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!apiKey.trim() || loading) return;
    handlePostMastershopIntegration(apiKey);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="relative flex w-full max-w-[850px] flex-col overflow-hidden rounded-xl bg-white shadow-xl md:flex-row">
        <button
          onClick={() => setModalOpenMastershop(false)}
          className="absolute right-4 top-4 z-10 p-2 text-gray-400 hover:text-gray-600 md:hidden"
        >
          <X size={24} />
        </button>

        <div className="flex flex-1 flex-col p-6 md:p-8">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#f3ffe9] ring-1 ring-lime-100">
              <img src={MASTERSHOP_LOGO} alt="Mastershop" className="h-8 w-8 object-contain" />
            </div>
            <h2 className="text-2xl font-bold leading-tight text-gray-900">
              {dictionary["mastershop-connect-title"]?.[language] || "Conecta tu cuenta Mastershop"}
            </h2>
          </div>

          <p className="mb-6 text-sm leading-relaxed text-gray-600">
            {dictionary["mastershop-connect-description"]?.[language] ||
              "Conecta Mastershop para consultar productos asociados y generar órdenes automáticamente desde tus embudos."}
          </p>

          <div className="mb-6 space-y-3">
            <div className="flex items-start gap-3">
              <PackageSearch className="mt-0.5 h-5 w-5 shrink-0 text-lime-600" />
              <span className="text-sm text-gray-700">
                {dictionary["mastershop-feature-products"]?.[language] || "Consulta productos asociados a tu comercio"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Box className="mt-0.5 h-5 w-5 shrink-0 text-lime-600" />
              <span className="text-sm text-gray-700">
                {dictionary["mastershop-feature-match"]?.[language] || "Vincula productos del embudo con productos Mastershop"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-lime-600" />
              <span className="text-sm text-gray-700">
                {dictionary["mastershop-feature-orders"]?.[language] || "Genera órdenes automáticamente al recibir ventas"}
              </span>
            </div>
          </div>

          <div className="mt-auto border-t border-gray-100 pt-4">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  {dictionary["mastershop-api-key"]?.[language] || "API key de Mastershop"}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder={dictionary["example-mastershop-api-key"]?.[language] || "Pega tu API key"}
                    className="w-full rounded-lg border border-gray-300 p-2.5 pl-10 text-sm transition-all focus:border-lime-500 focus:outline-none focus:ring-2 focus:ring-lime-100"
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-gray-400">
                  {dictionary["mastershop-credentials-hint"]?.[language] ||
                    "Usa la API key del comercio Mastershop que tendrá productos y órdenes asociados."}
                </p>
              </div>

              <div className="mt-2 flex gap-3">
                <button
                  type="submit"
                  disabled={!apiKey.trim() || loading}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {dictionary["connecting"][language]}
                    </>
                  ) : (
                    dictionary["connect"][language]
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpenMastershop(false)}
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  {dictionary["cancel"][language]}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="relative hidden w-[200px] shrink-0 items-center justify-center border-l border-gray-100 bg-gradient-to-b from-[#f3ffe9] to-white px-4 py-6 md:flex">
          <button
            onClick={() => setModalOpenMastershop(false)}
            className="absolute right-4 top-4 z-10 p-2 text-gray-400 transition-colors hover:text-gray-600"
          >
            <X size={18} />
          </button>

          <div className="flex h-full w-full flex-col items-center justify-center gap-8">
            <div className="relative z-10 flex h-24 w-24 items-center justify-center rounded-[24px] border border-lime-100 bg-white p-5 shadow-[0_4px_20px_rgba(132,204,22,0.18)]">
              <img src={FLUXI_LOGO} alt="Fluxi" className="h-auto w-full object-contain" />
            </div>

            <div className="flex flex-col items-center gap-1 text-lime-300">
              <ChevronDown strokeWidth={3} size={24} />
              <ChevronDown strokeWidth={3} size={24} />
              <ChevronDown strokeWidth={3} size={24} />
            </div>

            <div className="relative z-10 flex h-24 w-24 items-center justify-center rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
              <img src={MASTERSHOP_LOGO} alt="Mastershop" className="h-12 w-12 object-contain" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMastershopIntegration;
